/** @file tileBucketFill: flood fill of one colour region of a tile (4-connected, exact colour match). */
import type { Image, Rgba } from '@zilo/pose3d-image-io/types/image';
import { tileColorAt } from './tileColorAt';

/** Whether the pixel at byte offset `i` has exactly `color`. */
const isColor = (data: Uint8ClampedArray, i: number, color: Rgba): boolean =>
  data[i] === color[0] && data[i + 1] === color[1] && data[i + 2] === color[2] && data[i + 3] === color[3];

/** The 4-connected neighbours of pixel index `p` inside a `width` x `height` image. */
function neighbours(p: number, width: number, height: number): number[] {
  const out: number[] = [];
  const x = p % width;
  if (x > 0) out.push(p - 1);
  if (x < width - 1) out.push(p + 1);
  if (p >= width) out.push(p - width);
  if (p < width * (height - 1)) out.push(p + width);
  return out;
}

/**
 * Replaces the 4-connected region of pixels that have exactly the colour of (`x`, `y`) with `color`. Returns whether
 * anything changed (false outside the image or when the region already has `color`).
 */
export function tileBucketFill(image: Image, x: number, y: number, color: Rgba): boolean {
  const { width, height, data } = image;
  const target = tileColorAt(image, x, y);
  if (target === null || isColor(data, (y * width + x) * 4, color)) return false;
  const stack: number[] = [y * width + x];
  for (let p = stack.pop(); p !== undefined; p = stack.pop()) {
    if (!isColor(data, p * 4, target)) continue;
    data.set(color, p * 4);
    stack.push(...neighbours(p, width, height));
  }
  return true;
}
